import type { HoComponents } from '@pos/api-client';
import { hoApi } from '../api';
import { providerApi, type ProviderAccount } from '../delivery/deliveryApi';

export type MessageTemplate = HoComponents['schemas']['MessageTemplateDto'];
export type MessageTemplateInput = HoComponents['schemas']['MessageTemplateInput'];
export type CustomerMessage = HoComponents['schemas']['CustomerMessageDto'];
export type MessageRoute = HoComponents['schemas']['MessageRouteDto'];
export type BrandEmail = HoComponents['schemas']['BrandEmailDto'];
export type BrandEmailInput = HoComponents['schemas']['BrandEmailInput'];
export type EmailSender = HoComponents['schemas']['EmailSenderDto'];
export type EmailLink = HoComponents['schemas']['EmailLink'];

/** The server checks these again: online ordering's admins set the brands' customer messages (ADR 0072). */
export const MESSAGING_READ = 'ho.online.messaging.read';
export const MESSAGING_MANAGE = 'ho.online.messaging.manage';

/** The messages a customer can receive about an online order, in the order they happen. */
export const MESSAGE_EVENTS = [
  'order_accepted',
  'order_ready',
  'order_on_delivery',
  'order_completed',
  'order_not_taken',
  'reservation_confirmed',
  'reservation_declined',
  'reservation_cancelled',
  'login_code',
  'points_expiring', // a member's points expire soon (ADR 0125)
] as const;
export type MessageEvent = (typeof MESSAGE_EVENTS)[number];

/** A marketing email's template (ADR 0125): named, several per brand and language, sent by campaigns, never for an event. */
export const MARKETING_EVENT = 'marketing';
export const TEMPLATE_EVENTS = [...MESSAGE_EVENTS, MARKETING_EVENT] as const;
export type TemplateEvent = (typeof TEMPLATE_EVENTS)[number];

/** The channels a message can go by; without a channel order of the company's own, tried in this order (ADR 0125). */
export const MESSAGE_CHANNELS = ['zalo_zns', 'sms', 'email'] as const;
export type MessageChannel = (typeof MESSAGE_CHANNELS)[number];

/** The values a message can use: as `{name}` in an SMS, as the parameter names of a ZNS template. */
export const MESSAGE_VALUES: Record<TemplateEvent, readonly string[]> = {
  order_accepted: ['order_number', 'store_name', 'prep_minutes'],
  order_ready: ['order_number', 'store_name'],
  order_on_delivery: ['order_number', 'store_name'],
  order_completed: ['order_number', 'store_name'],
  order_not_taken: ['order_number', 'store_name', 'reason'],
  reservation_confirmed: ['reference', 'store_name', 'time', 'party_size'], // a table booked online (ADR 0092)
  reservation_declined: ['reference', 'store_name', 'time', 'party_size', 'reason'],
  reservation_cancelled: ['reference', 'store_name', 'time', 'party_size', 'reason'],
  login_code: ['code', 'minutes'], // a customer's sign-in code (ADR 0075); never shown in the message log
  points_expiring: ['points', 'expiry_date', 'club_name', 'account_no'], // ADR 0125
  marketing: [], // campaigns add their own values; the unsubscribe link is always added by the server
};

/**
 * The values the preview fills placeholders with, as the server's test message does (CustomerMessageLog.SendTestAsync), so staff
 * see a realistic email. The store and the reason are made-up samples.
 */
export const SAMPLE_VALUES: Readonly<Record<string, string>> = {
  order_number: 'TEST-0001',
  store_name: "Al Fresco's Thảo Điền",
  prep_minutes: '20',
  reason: 'Một số món đã hết.',
  reference: 'R-0001',
  time: '19:30',
  party_size: '4',
  code: '123456',
  minutes: '5',
  points: '120',
  expiry_date: '01/12/2026',
  club_name: 'Rewards+',
  account_no: 'A00000001',
};

/** An SMS of up to three parts (the server's limit). */
export const SMS_MAX = 480;

export const templatesKey = (brandId: string) => ['ho', 'messaging', 'templates', brandId] as const;

export async function listTemplates(brandId: string): Promise<MessageTemplate[]> {
  const { data, error } = await hoApi.GET('/api/v1/integration/messaging/templates', {
    params: { query: { brandId } },
  });
  if (!data) throw error;
  return data;
}

export async function saveTemplate(id: string | undefined, input: MessageTemplateInput): Promise<MessageTemplate> {
  const result = id
    ? await hoApi.PUT('/api/v1/integration/messaging/templates/{id}', { params: { path: { id } }, body: input })
    : await hoApi.POST('/api/v1/integration/messaging/templates', { body: input });
  if (!result.data) throw result.error;
  return result.data;
}

export async function deleteTemplate(id: string): Promise<void> {
  const { error, response } = await hoApi.DELETE('/api/v1/integration/messaging/templates/{id}', {
    params: { path: { id } },
  });
  if (!response.ok) throw error;
}

/** What happened to a message; the log can be filtered by it. */
export const MESSAGE_STATUSES = ['pending', 'sending', 'sent', 'failed', 'skipped', 'dead'] as const;
export type MessageStatus = (typeof MESSAGE_STATUSES)[number];

export const messagesKey = (brandId: string, status: string, page: number, pageSize: number) =>
  ['ho', 'messaging', 'messages', brandId, status, page, pageSize] as const;

/** A brand's customer messages, newest first; the phone is masked by the server. */
export async function listMessages(brandId: string, status: string, page: number, pageSize: number) {
  const { data, error } = await hoApi.GET('/api/v1/integration/messaging/messages', {
    params: { query: { brandId, status: status || undefined, page, pageSize } },
  });
  if (!data) throw error;
  return data;
}

/** Queues a test of a template's message to a phone, with sample values (test mode delivers nothing). */
export async function sendTestMessage(templateId: string, recipient: string): Promise<string> {
  const { data, error } = await hoApi.POST('/api/v1/integration/messaging/templates/{id}/test', {
    params: { path: { id: templateId } },
    body: { recipient },
  });
  if (!data) throw error;
  return data.messageId;
}

/** Who sends a brand's email: the SMTP profiles (provider accounts) of these providers (ADR 0125); the simulator in test mode. */
export const EMAIL_PROVIDERS = ['messaging_smtp_email', 'messaging_simulator_email'] as const;

const messagingProviders = providerApi('messaging');
export const messagingAccountsKey = messagingProviders.accountsKey;

/** The company's email accounts (SMTP profiles and test-mode ones), for the SMTP profile pickers. */
export async function listEmailAccounts(): Promise<ProviderAccount[]> {
  return (await messagingProviders.listAccounts()).filter((a) => (EMAIL_PROVIDERS as readonly string[]).includes(a.providerCode));
}

export const routesKey = ['ho', 'messaging', 'routes'] as const;

/** Every message with the channels it goes by, first tried first; `isDefault` when the company hasn't set its own. */
export async function listRoutes(): Promise<MessageRoute[]> {
  const { data, error } = await hoApi.GET('/api/v1/integration/messaging/routes');
  if (!data) throw error;
  return data;
}

export async function saveRoute(eventCode: string, channels: string[], version: number | string): Promise<MessageRoute> {
  const { data, error } = await hoApi.PUT('/api/v1/integration/messaging/routes/{eventCode}', {
    params: { path: { eventCode } },
    body: { channels, version },
  });
  if (!data) throw error;
  return data;
}

export const brandEmailKey = (brandId: string) => ['ho', 'messaging', 'brand-email', brandId] as const;

/** A brand's email settings: the layout of every email and the senders (version 0 while none are saved). */
export async function getBrandEmail(brandId: string): Promise<BrandEmail> {
  const { data, error } = await hoApi.GET('/api/v1/integration/messaging/brand-email/{brandId}', { params: { path: { brandId } } });
  if (!data) throw error;
  return data;
}

export async function saveBrandEmail(brandId: string, input: BrandEmailInput): Promise<BrandEmail> {
  const { data, error } = await hoApi.PUT('/api/v1/integration/messaging/brand-email/{brandId}', { params: { path: { brandId } }, body: input });
  if (!data) throw error;
  return data;
}
